import { useState } from 'react';
import { Button } from '@busbook/ui';
import { useInstallPrompt } from '../lib/install-prompt.js';
import { useT } from '../i18n/index.js';

/**
 * Offering the home screen, beside the browser's own offer rather than instead of it.
 *
 * The browser asks first now (see lib/install-prompt): its install UI is no longer
 * suppressed, so a first-time visitor is offered the app without this card being involved.
 * What is left for the card is what the browser does not do — open the install dialog from
 * a button, since `prompt()` needs a user action; be visible on a browser whose own offer
 * is an address-bar icon nobody notices; and carry the share-sheet steps on iOS, which
 * offers nothing at all.
 *
 * Placed where a passenger has just been given a reason: a ticket they will want to reach
 * at a bus station with no signal, and the trips list they come back to. Never twice in a
 * month after a "not now" — a prompt that nags is one people learn to dismiss without
 * reading.
 */
export function InstallPrompt({ appName }: { appName: string }) {
  const t = useT();
  const { offer, install, dismiss } = useInstallPrompt();
  const [busy, setBusy] = useState(false);

  if (offer === null) return null;

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)' }}
    >
      <div className="flex items-start gap-3">
        <PhoneGlyph />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{t('ip.title', { app: appName })}</p>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
            {t('ip.why')}
          </p>

          {offer === 'ios' ? (
            // No button is possible here: iOS only installs from its own share sheet, so the
            // honest thing is to say where it is rather than offer a control that cannot work.
            <ol
              className="mt-3 flex flex-col gap-1 text-sm"
              style={{ color: 'var(--color-muted)' }}
            >
              <li>1. {t('ip.iosShare')}</li>
              <li>2. {t('ip.iosAdd')}</li>
            </ol>
          ) : null}

          <div className="mt-3 flex flex-wrap gap-2">
            {offer === 'prompt' && (
              <Button
                size="sm"
                loading={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    // Whatever they choose, the browser will not give us the event again;
                    // the hook drops it, so this card disappears either way.
                    await install();
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {t('ip.install')}
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={dismiss}>
              {t('ip.notNow')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PhoneGlyph() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="mt-0.5 shrink-0"
      style={{ color: 'var(--color-primary)' }}
    >
      <rect x="6" y="2.5" width="12" height="19" rx="2.5" />
      <path d="M11 18.5h2" />
      <path d="M12 7v6M9.5 10.5 12 13l2.5-2.5" />
    </svg>
  );
}
